import { animated, useSpring } from "@react-spring/web"
import { useDrag } from "@use-gesture/react"
import { ReactNode, useEffect } from "react"

interface SwipeToCloseProps {
  children: ReactNode | (({ onClose }: { onClose: () => void }) => ReactNode)
  onClose: () => void
  isOpen: boolean
  threshold?: number
  velocityThreshold?: number
  className?: string
  minStartX?: number // Minimum X position to start swipe (from center)
}

export const SwipeToClose = ({
  children,
  onClose,
  isOpen,
  threshold = 150,
  velocityThreshold = 0.5,
  className = "",
  minStartX = 100
}: SwipeToCloseProps) => {
  // Spring animation for smooth movement
  const [{ x }, api] = useSpring(() => ({
    x: 0,
    config: { tension: 400, friction: 40 }
  }))

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      api.start({ x: 0 })
    }
  }, [isOpen, api])

  const handleClose = () => {
    api.start({ x: 0 })
    onClose()
  }

  // Use gesture hook for smoother interactions
  const bind = useDrag(
    ({ active, movement: [mx], velocity: [vx], initial: [startX], cancel }) => {
      // Check if swipe started from valid area
      if (startX < minStartX || startX > window.innerWidth - minStartX) {
        cancel()
        return
      }

      // Only allow rightward movement
      const clampedMx = Math.max(0, mx)

      if (active) {
        api.start({ x: clampedMx, immediate: true })
      } else {
        // On release
        const screenWidth = window.innerWidth
        const halfScreen = screenWidth / 2

        // Close modal if:
        // 1. Dragged beyond threshold
        // 2. Dragged past half screen (50%)
        // 3. High velocity swipe
        if (clampedMx > threshold || clampedMx > halfScreen || vx > velocityThreshold) {
          handleClose()
        } else {
          // Reset to original position
          api.start({ x: 0 })
        }
      }
    },
    {
      axis: "x",
      bounds: { left: 0, right: window.innerWidth },
      rubberband: 0.1
    }
  )

  return (
    <animated.div
      {...bind()}
      className={`${className}`}
      style={{
        touchAction: "pan-y",
        transform: x.to((x) => `translateX(${x}px)`)
      }}
    >
      {typeof children === "function"
        ? (children as ({ onClose }: { onClose: () => void }) => ReactNode)({
            onClose: handleClose
          })
        : children}
    </animated.div>
  )
}
